import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, Upload, Trash2, Search, ExternalLink } from "lucide-react";
import { fileUrl } from "@/lib/placeholders";

/**
 * Il catalogo delle schede di sicurezza allegate al POS.
 *
 * Si caricano trascinando dentro i PDF tutti insieme: il codice, la famiglia e
 * il titolo li ricava dal nome del file, quindi non si digita niente. Prima di
 * scrivere mostra cosa entrerebbe, perche' settantatre file alla volta non si
 * controllano a occhio dopo.
 */
export default function CatalogoSchedePos() {
  const [aperto, setAperto] = useState(false);
  const [schede, setSchede] = useState([]);
  const [famiglie, setFamiglie] = useState([]);
  const [cerca, setCerca] = useState("");
  const [famiglia, setFamiglia] = useState("");
  const [verifica, setVerifica] = useState(null);
  const [busy, setBusy] = useState(false);
  const campo = useRef(null);
  const daCaricare = useRef([]);

  const carica = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        api.get("/pos/schede"), api.get("/pos/famiglie"),
      ]);
      setSchede(s.data);
      setFamiglie(f.data);
    } catch { toast.error("Errore nel caricamento del catalogo"); }
  }, []);

  useEffect(() => { if (aperto) carica(); }, [aperto, carica]);

  const manda = async (files, soloVerifica) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    fd.append("solo_verifica", soloVerifica ? "true" : "false");
    const { data } = await api.post("/pos/schede/importa", fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const scegli = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      daCaricare.current = files;
      setVerifica(await manda(files, true));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella lettura dei file");
    } finally { setBusy(false); }
  };

  const conferma = async () => {
    setBusy(true);
    try {
      const esito = await manda(daCaricare.current, false);
      toast.success(
        `${esito.nuove.length} nuove, ${esito.aggiornate.length} aggiornate`);
      setVerifica(null);
      daCaricare.current = [];
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally { setBusy(false); }
  };

  const elimina = async (s) => {
    if (!window.confirm(`Togliere ${s.codice} dal catalogo?`)) return;
    try {
      await api.delete(`/pos/schede/${s.id}`);
      toast.success(`${s.codice} rimossa`);
      carica();
    } catch { toast.error("Errore"); }
  };

  const visibili = schede.filter((s) => {
    if (famiglia && s.famiglia !== famiglia) return false;
    if (!cerca.trim()) return true;
    const t = cerca.trim().toLowerCase();
    return (s.titolo + " " + s.codice).toLowerCase().includes(t);
  });

  return (<>
    <Button variant="outline" className="h-11" onClick={() => setAperto(true)}
            data-testid="schede-pos-btn">
      <ClipboardList className="w-4 h-4 mr-2" /> Schede POS
    </Button>

    {aperto && (
      <Dialog open onOpenChange={() => { setAperto(false); setVerifica(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schede di sicurezza del POS</DialogTitle>
          </DialogHeader>

          {verifica ? (
            <div className="space-y-3">
              <p className="text-sm">Ecco cosa entrerebbe. Niente è ancora stato salvato.</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Nuove", verifica.nuove.length, "text-emerald-700 bg-emerald-50 border-emerald-200"],
                  ["Da aggiornare", verifica.aggiornate.length, "text-sky-700 bg-sky-50 border-sky-200"],
                  ["Scartate", verifica.scartate.length, "text-amber-800 bg-amber-50 border-amber-200"]]
                  .map(([nome, quante, classe]) => (
                    <div key={nome} className={`border rounded p-2 ${classe}`}>
                      <div className="text-xl font-black tabular-nums">{quante}</div>
                      <div className="text-[10px] uppercase tracking-wider">{nome}</div>
                    </div>
                  ))}
              </div>
              {verifica.scartate.length > 0 && (
                <div className="border border-amber-200 rounded divide-y divide-amber-100 max-h-40 overflow-y-auto">
                  {verifica.scartate.map((s, i) => (
                    <div key={i} className="p-2 text-xs">
                      <span className="font-semibold">{s.file}</span>
                      <span className="text-amber-800"> — {s.perche}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-52 overflow-y-auto">
                {[...verifica.nuove, ...verifica.aggiornate].map((s, i) => (
                  <div key={i} className="p-1.5 flex items-center gap-2 text-xs">
                    <span className="font-semibold w-16 flex-shrink-0">{s.codice}</span>
                    <span className="truncate">{s.titolo}</span>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setVerifica(null)}>Annulla</Button>
                <Button onClick={conferma} disabled={busy}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="conferma-schede">
                  {busy ? "Carico..." : "Carica nel catalogo"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <input ref={campo} type="file" accept=".pdf" multiple className="hidden"
                     onChange={scegli} />
              <div className="flex gap-2 flex-wrap items-center">
                <Button variant="outline" onClick={() => campo.current?.click()}
                        disabled={busy} data-testid="carica-schede">
                  <Upload className="w-3.5 h-3.5 mr-2" />
                  {busy ? "Leggo..." : "Carica i PDF delle schede"}
                </Button>
                <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white"
                        value={famiglia} onChange={(e) => setFamiglia(e.target.value)}>
                  <option value="">Tutte le famiglie</option>
                  {famiglie.map((f) => (
                    <option key={f.codice} value={f.codice}>
                      {f.codice} · {f.nome} ({f.quante})
                    </option>
                  ))}
                </select>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={cerca} onChange={(e) => setCerca(e.target.value)}
                         placeholder="Cerca..."
                         className="h-9 border border-slate-200 rounded-full pl-8 pr-3 text-xs w-40" />
                </div>
                <span className="text-xs text-slate-400 ml-auto">
                  {visibili.length} di {schede.length}
                </span>
              </div>

              <p className="text-xs text-slate-500">
                Puoi selezionarli tutti insieme. Codice e titolo si leggono dal nome del
                file, nella forma <span className="font-mono">SCAM 001 Autocarro.pdf</span>.
                Ricaricando una scheda revisionata si sostituisce il file e il codice resta
                quello.
              </p>

              {schede.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded p-8 text-center text-sm text-slate-500">
                  Il catalogo è vuoto. Carica i PDF delle schede per cominciare.
                </div>
              ) : (
                <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {visibili.map((s) => (
                    <div key={s.id} className="p-1.5 flex items-center gap-2 text-xs">
                      <span className="font-semibold w-16 flex-shrink-0">{s.codice}</span>
                      <span className="flex-1 truncate" title={s.titolo}>{s.titolo}</span>
                      <a href={fileUrl(s.file_url)} target="_blank" rel="noreferrer"
                         className="text-slate-400 hover:text-[#0067b2] px-1" title="Apri">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <button type="button" onClick={() => elimina(s)}
                              className="text-red-500 hover:text-red-700 px-1" title="Togli">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <DialogFooter>
                <Button onClick={() => setAperto(false)}
                        className="bg-orange-500 hover:bg-orange-600 text-white">
                  Chiudi
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    )}
  </>);
}
